/**
 * Copyright (c) 2019, Tidepool Project
 *
 * This program is free software; you can redistribute it and/or modify it under
 * the terms of the associated License, which is identical to the BSD 2-Clause
 * License as published by the Open Source Initiative at opensource.org.
 *
 * This program is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS
 * FOR A PARTICULAR PURPOSE. See the License for more details.
 *
 * You should have received a copy of the License along with this program; if
 * not, you can obtain one from Tidepool Project at tidepool.org.
 */

import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Container, Typography, Radio, RadioGroup, FormControl, FormControlLabel } from '@material-ui/core';
import PatientInfoSnippet from './PatientInfoSnippet.js';
import HelpPopup from './HelpPopup.js';
import * as _ from '../../../data/constants.js';

const useStyles = makeStyles(theme => ({
  box: {
    'background-color': '#f9f9f9',
    'margin-bottom': '24px',
    'margin-top': '24px',
    'margin-left': '12px',
    'margin-right': '12px',
    padding: '5px',
    display: 'flex',
    'align-items': 'flex-start'
  },
  titles: {
    'margin-left': '24px',
    'margin-right': '24px',
    'margin-bottom': '24px'
  },
  biggerTitles: {
    'margin-left': '12px',
    'margin-bottom': '24px',
    'margin-top': '12px'
  },
  border: {
    'border-radius': '10px',
    border: '2px solid #EDEDED',
    padding: '8px',
    margin: '24px'
  },
  aligned: {
    display: 'flex',
    flexDirection: 'column',
    'margin-left': 'auto',
    'margin-right': 'auto',
    'align-items': 'center'
  },
  page: {
    width: '80%',
    display: 'flex-box',
    flexDirection: 'column',
    'margin-left': 'auto',
    'margin-right': 'auto',
    'align-items': 'center'
  },
  innerElements: {
    'margin-left': '48px',
    'margin-right': '48px'
  },
  inline: {
    display: 'flex',
    flexDirection: 'row',
    'align-items': 'center'
  },
  helpIcon: {
    'margin-left': '12px'
  },
  description: {
    'margin-left': '12px',
    'margin-right': '12px'
  }
}));

/**
 * PrescriptionPlan Component
 *
 * This component consitutes page 2 of the prescription form, it contains the functionality for choosing initial
 * therapy settings and whether or not the patient will start on suspend-only or closed-loop mode
 */
const PrescriptionPlan = props => {
  // scroll to top on open
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const classes = useStyles();

  return (
    <form onSubmit={props.handleSubmit}>
      <Container>
        <div className={classes.page}>
          <div>
            <div className={classes.border}>
              <PatientInfoSnippet
                patient={props.values.firstName + ' ' + props.values.lastName}
                dateOfBirth={props.values.birthday}
                email={props.values.email}
              />
            </div>
            <div className={classes.border}>
              <div className={classes.biggerTitles}>
                <Typography variant="h6" color="textSecondary">
                  Tidepool Loop initial therapy settings
                </Typography>
              </div>
              <div className={classes.innerElements}>
                <Typography variant="body2" color="textSecondary">
                  {_.TherapyLanguage}
                </Typography>
              </div>
              <div className={classes.box}>
                <div className={classes.innerElements}>
                  <FormControl>
                    <RadioGroup name="therapySetting" value={props.values.therapySetting} onChange={props.handleChange}>
                      <FormControlLabel
                        value="initial"
                        control={<Radio color="default" />}
                        label={<Typography color="textSecondary">Use initial pump settings order</Typography>}
                      />
                      {/*<FormControlLabel
                        value="tps"
                        control={<Radio color="default"/>}
                        label={<Typography color="textSecondary">Transfer pump settings</Typography>}
                      />*/}
                      <FormControlLabel
                        value="cpt"
                        control={<Radio color="default" />}
                        label={
                          <Typography color="textSecondary">
                            CPT to determine pump start settings / parameters
                          </Typography>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                </div>
              </div>
            </div>
            <div className={classes.border}>
              <div className={classes.biggerTitles}>
                <div className={classes.inline}>
                  <div>
                    <Typography variant="h6" color="textSecondary">
                      Tidepool Loop Mode
                    </Typography>
                  </div>
                  <div className={classes.helpIcon}>
                    <HelpPopup helpMessage={_.TidepoolLoopModePopup} />
                  </div>
                </div>
              </div>
              <div className={classes.box}>
                <div className={classes.aligned}>
                  <Typography color="error" variant="subtitle1">
                    {props.touched.loopMode ? props.errors.loopMode : ''}
                  </Typography>
                  <FormControl>
                    <RadioGroup name="loopMode" value={props.values.loopMode} onChange={props.handleChange} row>
                      <FormControlLabel
                        value="som"
                        control={<Radio color="default" />}
                        label={<Typography color="textSecondary">Suspend-only mode</Typography>}
                      />
                      <FormControlLabel
                        value="clm"
                        control={<Radio color="default" />}
                        label={<Typography color="textSecondary">Closed-loop mode</Typography>}
                      />
                    </RadioGroup>
                  </FormControl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </form>
  );
};

export default PrescriptionPlan;
